import {
  activeNamespaceField,
  VulnerabilityScanningFormValues,
} from '@/react/portainer/policies/components/kubernetes/vulnerability-scanning/types';
import { pluralize, toTitleCase } from '@/react/common/string-utils';

import { FormSection } from '@@/form-components/FormSection';
import { Badge } from '@@/Badge';

import { PolicyTableWrapper } from './PolicyTableWrapper';

interface Props {
  formData: VulnerabilityScanningFormValues;
}

export function VulnerabilityScanningPolicyReadOnly({ formData }: Props) {
  const mode = formData.namespaceScopeMode;
  const namespaces = formData[activeNamespaceField(mode)];
  const scopeLabel =
    mode === 'all-except' ? 'All namespaces except' : 'Only these namespaces';

  return (
    <>
      <FormSection title="Scanning">
        <PolicyTableWrapper
          caption="Vulnerability scanning schedule"
          data-cy="vulnerability-scanning-policy-readonly-table"
        >
          <tbody className="divide-y divide-gray-5 th-highcontrast:divide-gray-7 th-dark:divide-gray-7">
            <tr className="bg-gray-2 th-highcontrast:bg-black th-dark:bg-gray-iron-10">
              <td className="px-4 py-3 text-sm font-medium">Rescan interval</td>
              <td className="px-4 py-3 text-sm">
                Every {formData.rescanIntervalValue}{' '}
                {pluralize(
                  formData.rescanIntervalValue,
                  formData.rescanIntervalUnit.replace(/s$/, '')
                )}
              </td>
            </tr>
            <tr className="bg-gray-2 th-highcontrast:bg-black th-dark:bg-gray-iron-10">
              <td className="px-4 py-3 text-sm font-medium">
                Maximum concurrent scans
              </td>
              <td className="px-4 py-3 text-sm">
                {formData.maxConcurrentScans}
              </td>
            </tr>
            <tr className="bg-gray-2 th-highcontrast:bg-black th-dark:bg-gray-iron-10">
              <td className="px-4 py-3 text-sm font-medium">
                Recorded severities
              </td>
              <td className="px-4 py-3 text-sm">
                {formData.severities.map(toTitleCase).join(', ')}
              </td>
            </tr>
            <tr className="bg-gray-2 th-highcontrast:bg-black th-dark:bg-gray-iron-10">
              <td className="px-4 py-3 text-sm font-medium">
                Operator namespace
              </td>
              <td className="px-4 py-3 text-sm">{formData.targetNamespace}</td>
            </tr>
          </tbody>
        </PolicyTableWrapper>
      </FormSection>

      <FormSection title="Namespace scope">
        <p className="text-muted text-sm">{scopeLabel}</p>
        {namespaces.length === 0 ? (
          <p className="text-muted text-sm">
            Every namespace in the cluster is scanned.
          </p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-2">
            {namespaces.map((namespace) => (
              <Badge key={namespace} type="info">
                {namespace}
              </Badge>
            ))}
          </div>
        )}
      </FormSection>
    </>
  );
}
